'use client'

import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import type { WhoAmIClient } from '@tebuto/api/clients/models/WhoAmIClient'
import { EMAIL_REGEX, isValidDachZip, PHONE_REGEX } from '@tebuto/functions/validate'
import { CurrentClientContext } from '@tebuto/guards/current-client-context'
import RequestGate from '@tebuto/layout/RequestGate'
import { ComponentSpinner } from '@tebuto/layout/Spinner'
import { cn } from '@tebuto/lib/utils'
import { RuntimeEnvProvider } from '@tebuto/providers/RuntimeEnvProvider'
import TranslationProvider from '@tebuto/providers/TranslationProvider'
import Booking from '@tebuto/widget/booking/Booking'
import { useContext, useMemo, useState } from 'react'
import { type ObjectShape, object, string } from 'yup'
import { ThemeProvider, type WidgetThemeConfig } from '../theme'
import '@tebuto/payment/checkout/checkout.css'
import '../themed-styles.css'
import BookingFailure from './BookingFailure'
import BookingSuccess from './BookingSuccess'
import { BookingWidgetContext, type ConfiguredCategory, type ConfiguredTherapist } from './context'
import { getBookingWidgetHooks, useFingerprint } from './hooks'
import PersonalDataFields from './PersonalDataFields'
import Watermark from './Watermark'
import { LoggedInBanner, LoginView, WidgetAuthContext, WidgetAuthProvider } from './widget-auth'
import type { WidgetSessionClient } from './widget-auth/types'

interface BookingWidgetProps {
    therapistUUID: string
    categories?: number[]
    includeSubusers?: boolean
    showQuickFilters?: boolean
    showLocationQuickFilter?: boolean
    showTherapistProfile?: boolean
    profileUrl?: string
    inheritFont?: boolean
    backgroundColor?: string
    border?: boolean
    center?: boolean
    compact?: boolean
    apiUrl?: string
    theme?: Partial<WidgetThemeConfig>
    configuredCategories?: ConfiguredCategory[]
    configuredTherapists?: ConfiguredTherapist[]
    publicBookingSiteId?: string
    /** When true (default), visitors pick a category first if multiple categories are available. */
    showCategorySelectionFirst?: boolean
}

export default function BookingWidget(props: BookingWidgetProps) {
    const apiUrl = props.apiUrl ?? (process.env.NEXT_PUBLIC_PUBLIC_API_URL as string)
    return (
        <WidgetAuthProvider apiUrl={apiUrl}>
            <BookingWidgetInner {...props} apiUrl={apiUrl} />
        </WidgetAuthProvider>
    )
}

function BookingWidgetInner({
    therapistUUID,
    categories,
    includeSubusers = false,
    showQuickFilters = false,
    showLocationQuickFilter = false,
    showTherapistProfile = false,
    profileUrl,
    inheritFont = false,
    backgroundColor,
    border = true,
    center = false,
    compact = false,
    apiUrl,
    theme,
    configuredCategories,
    configuredTherapists,
    publicBookingSiteId,
    showCategorySelectionFirst = true
}: BookingWidgetProps & { apiUrl: string }) {
    const { session } = useContext(WidgetAuthContext)
    const {
        useBookableEvents,
        useCheckBookingAvailability,
        useUnclaimBooking,
        usePaymentConfiguration,
        useGeneratePayPalClientToken,
        useCreateCheckoutOrder,
        useCreateCheckoutSession,
        useBookEvent,
        useParallelWidgetBootstrap
    } = getBookingWidgetHooks(apiUrl, { session })
    const fingerprint = useFingerprint()

    const effectiveCategoryFilter = useMemo(() => {
        if (categories && categories.length > 0) {
            return categories
        }
        if (configuredCategories && configuredCategories.length > 0) {
            return configuredCategories.map((category) => category.id)
        }
        return undefined
    }, [categories, configuredCategories])

    const effectiveIncludeSubusers = useMemo(
        () => includeSubusers || (configuredCategories?.some((category) => category.isFromSubaccount) ?? false),
        [includeSubusers, configuredCategories]
    )

    const { therapist, prefetchedPublicEvents, showLoading, error, mutate } = useParallelWidgetBootstrap(
        therapistUUID,
        effectiveCategoryFilter,
        effectiveIncludeSubusers,
        fingerprint
    )

    const [requirePhoneNumber, setRequirePhoneNumber] = useState(false)
    const [requireAddress, setRequireAddress] = useState(false)
    const [requireBirthdate, setRequireBirthdate] = useState(false)
    const [bookingNote, setBookingNote] = useState<string | null>(null)

    const isAuthenticated = !!session

    const additionalBookingValidationSchemaFields = useMemo(() => {
        if (isAuthenticated) {
            return {}
        }

        const fields: ObjectShape = {
            firstName: string().required('Bitte geben Sie Ihren Vornamen an.'),
            lastName: string().required('Bitte geben Sie Ihren Nachnamen an.'),
            email: string()
                .matches(EMAIL_REGEX, 'Bitte geben Sie eine gültige E-Mail-Adresse an.')
                .required('Bitte geben Sie eine gültige E-Mail-Adresse an.')
        }

        if (requirePhoneNumber) {
            fields.phoneNumber = string().required('Pflichtfeld').matches(PHONE_REGEX, 'Ungültige Telefonnummer')
        }

        if (requireAddress) {
            fields.address = object().shape({
                streetAndNumber: string().required('Pflichtfeld'),
                city: object().shape({
                    zip: string()
                        .required('Pflichtfeld')
                        .test('zip', 'Ungültige Postleitzahl', (value) => isValidDachZip(value)),
                    name: string().required('Pflichtfeld')
                })
            })
        }

        if (requireBirthdate) {
            fields.birthdate = string().required('Pflichtfeld')
        }

        return fields
    }, [requirePhoneNumber, requireAddress, requireBirthdate, isAuthenticated])

    const configuratorFixedCategoryId = useMemo(
        () => (configuredCategories?.length === 1 ? configuredCategories[0].id : undefined),
        [configuredCategories]
    )
    const skipInitialCategorySelection = !showCategorySelectionFirst

    const additionalInitialValues = useMemo(() => {
        const initialValues: Record<string, any> = {
            termsAndConditionsConfirmed: false
        }

        if (isAuthenticated && session?.client) {
            initialValues.firstName = session.client.firstName
            initialValues.lastName = session.client.lastName
            initialValues.email = session.client.email ?? ''

            if (session.client.phoneNumber) {
                initialValues.phoneNumber = session.client.phoneNumber
            }

            if (session.client.birthdate) {
                initialValues.birthdate = session.client.birthdate
            }

            if (session.client.address) {
                initialValues.address = {
                    streetAndNumber: session.client.address.streetAndNumber,
                    city: {
                        zip: session.client.address.city.zip,
                        name: session.client.address.city.name
                    }
                }
            }
        } else if (!isAuthenticated) {
            initialValues.firstName = ''
            initialValues.lastName = ''
            initialValues.email = ''

            if (requirePhoneNumber) {
                initialValues.phoneNumber = ''
            }

            if (requireAddress) {
                initialValues.address = {
                    streetAndNumber: '',
                    city: {
                        zip: '',
                        name: ''
                    }
                }
            }

            if (requireBirthdate) {
                initialValues.birthdate = ''
            }
        }

        return initialValues
    }, [requirePhoneNumber, requireAddress, requireBirthdate, isAuthenticated, session])

    const defaultTheme: WidgetThemeConfig = {
        primaryColor: '#00b4a9',
        backgroundColor: backgroundColor || '#ffffff',
        textPrimary: '#374151',
        textSecondary: '#6b7280'
    }

    const mergedTheme: WidgetThemeConfig = theme
        ? {
              ...defaultTheme,
              ...theme,
              backgroundColor: backgroundColor || theme.backgroundColor || defaultTheme.backgroundColor,
              inheritFont: inheritFont || theme.inheritFont
          }
        : { ...defaultTheme, inheritFont }

    const currentClientContextValue = useMemo(
        () => (session ? mapWidgetSessionClientToContext(session.client) : null),
        [session]
    )

    return (
        <ThemeProvider theme={mergedTheme}>
            <RuntimeEnvProvider>
                <div
                    id="tebuto-booking-widget"
                    className={cn(
                        'tebuto-style-reset widget-container tebuto-checkout relative overflow-hidden flex flex-col max-w-5xl min-h-96 lg:h-152',
                        compact ? '' : 'pt-2 lg:pt-4',
                        border ? 'border rounded-md shadow-md widget-border px-4 lg:px-8' : '',
                        center ? 'mx-auto' : '',
                        inheritFont ? 'widget-inherit-font' : ''
                    )}
                    style={{ backgroundColor: mergedTheme.backgroundColor }}
                >
                    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="de">
                        <TranslationProvider>
                            <RequestGate
                                data={therapist && prefetchedPublicEvents ? therapist : undefined}
                                showLoading={showLoading}
                                error={error}
                                retry={mutate}
                                loadingComponent={
                                    <div className="flex w-full flex-1 items-center justify-center min-h-80 lg:min-h-0">
                                        <ComponentSpinner layout="block" />
                                    </div>
                                }
                            >
                                {(therapist) => (
                                    <BookingWidgetContext.Provider
                                        value={{
                                            therapistUUID,
                                            requirePhoneNumber,
                                            setRequirePhoneNumber,
                                            requireAddress,
                                            setRequireAddress,
                                            requireBirthdate,
                                            setRequireBirthdate,
                                            bookingNote,
                                            setBookingNote,
                                            publicBookingSiteId,
                                            configuredCategories,
                                            configuredTherapists
                                        }}
                                    >
                                        <CurrentClientContext.Provider value={currentClientContextValue}>
                                            <div className="flex flex-col w-full flex-1 min-h-0 gap-4">
                                                {isAuthenticated && <LoggedInBanner />}
                                                <Booking
                                                    therapist={therapist}
                                                    checkoutRestoreScopeKey={therapistUUID}
                                                    // eslint-disable-next-line react-hooks/rules-of-hooks -- useBookableEvents is a hook factory that returns a hook
                                                    // biome-ignore lint/correctness/useHookAtTopLevel: Hook factory pattern - returns a hook to be called at component top level
                                                    useBookableEvents={useBookableEvents(
                                                        effectiveCategoryFilter,
                                                        effectiveIncludeSubusers,
                                                        prefetchedPublicEvents,
                                                        fingerprint
                                                    )}
                                                    useCheckBookingAvailability={useCheckBookingAvailability}
                                                    useUnclaimBooking={useUnclaimBooking}
                                                    usePaymentConfiguration={usePaymentConfiguration}
                                                    useGeneratePayPalClientToken={useGeneratePayPalClientToken}
                                                    useCreateCheckoutOrder={useCreateCheckoutOrder}
                                                    useCreateCheckoutSession={useCreateCheckoutSession}
                                                    useBookEvent={useBookEvent}
                                                    requireCaptcha={false}
                                                    requireTermsConsent={true}
                                                    showQuickFilters={showQuickFilters}
                                                    showLocationQuickFilter={showLocationQuickFilter}
                                                    showTherapistProfile={showTherapistProfile}
                                                    profileUrl={profileUrl}
                                                    layout={requireAddress && !isAuthenticated ? 'large' : 'small'}
                                                    buildFailureComponent={(backToCalendar) => (
                                                        <BookingFailure backToCalendar={backToCalendar} />
                                                    )}
                                                    buildSuccessComponent={() => <BookingSuccess />}
                                                    buildLoginComponent={(onDone, onBack) => (
                                                        <LoginView onDone={onDone} onBack={onBack} />
                                                    )}
                                                    additionalBookingFieldsBefore={
                                                        isAuthenticated ? null : <PersonalDataFields />
                                                    }
                                                    additionalInitialValues={additionalInitialValues}
                                                    additionalBookingValidationSchemaFields={
                                                        additionalBookingValidationSchemaFields
                                                    }
                                                    bookingNote={bookingNote}
                                                    fixedCategoryId={configuratorFixedCategoryId}
                                                    skipInitialCategorySelection={skipInitialCategorySelection}
                                                    isClientAuthenticated={isAuthenticated}
                                                    onClickAlreadyHaveAccount={() => {
                                                        /* signal only — state transition handled by BookingEvent */
                                                    }}
                                                />
                                            </div>
                                        </CurrentClientContext.Provider>
                                        {therapist.showWatermark && <Watermark />}
                                    </BookingWidgetContext.Provider>
                                )}
                            </RequestGate>
                        </TranslationProvider>
                    </LocalizationProvider>
                </div>
            </RuntimeEnvProvider>
        </ThemeProvider>
    )
}

function mapWidgetSessionClientToContext(client: WidgetSessionClient): WhoAmIClient {
    return {
        id: client.id,
        firstName: client.firstName,
        lastName: client.lastName,
        email: client.email ?? '',
        gender: 'not_set' as WhoAmIClient['gender'],
        phoneNumber: client.phoneNumber,
        birthdate: client.birthdate,
        isActive: true,
        therapists: [],
        address: client.address
            ? ({
                  streetAndNumber: client.address.streetAndNumber,
                  additionalInformation: client.address.additionalInformation ?? null,
                  city: {
                      zip: client.address.city.zip,
                      name: client.address.city.name
                  }
              } as WhoAmIClient['address'])
            : null
    }
}
